import { useParams, useNavigate } from 'react-router-dom';
import { articleContentMap, homepageArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();

  const articleMeta = homepageArticles.find((a) => a.id === articleId);
  const articleData = articleId ? articleContentMap[articleId] : undefined;

  if (!articleMeta || !articleData) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-article-line text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-2">Article Not Found</h1>
          <p className="text-sm text-foreground-500 font-body mb-6">The article you are looking for does not exist or may have been moved.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Homepage
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="font-body whitespace-nowrap">Back to Hub</span>
          </button>
          <span className="text-sm font-bold text-foreground-900 font-heading whitespace-nowrap">
            hipobuyspread<span className="text-accent-500">.hair</span>
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-2.5 py-1 rounded-full text-xs font-body ${
              articleMeta.categoryColor === 'accent'
                ? 'bg-accent-100 text-accent-700'
                : 'bg-secondary-100 text-secondary-700'
            }`}>
              {articleMeta.category}
            </span>
            <span className="text-xs text-foreground-400 font-body">{articleMeta.date}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 font-heading leading-tight">
            {articleData.title}
          </h1>
          <p className="mt-4 text-base text-foreground-600 font-body leading-relaxed">
            {articleData.summary}
          </p>
        </header>

        <div className="prose-custom space-y-10">
          {articleData.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-4">
                {section.heading}
              </h2>
              <p className="text-sm md:text-base text-foreground-700 font-body leading-relaxed">
                {section.content}
              </p>
            </section>
          ))}
        </div>

        <div className="mt-14 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {articleData.faqs.map((faq) => (
              <details key={faq.question} className="group">
                <summary className="flex items-center justify-between cursor-pointer py-3 text-sm font-semibold text-foreground-800 font-body hover:text-foreground-950 transition-colors">
                  {faq.question}
                  <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                  <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                </summary>
                <p className="pb-3 text-sm text-foreground-600 font-body leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>

        <div className="mt-10 p-6 bg-accent-50 rounded-2xl border border-accent-100">
          <h3 className="text-base font-semibold text-accent-800 font-body mb-2">
            Related Categories
          </h3>
          <div className="flex flex-wrap gap-2 mt-3">
            {categories.slice(0, 8).map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-1.5 rounded-full bg-white border border-accent-200 text-xs text-accent-700 hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap font-body"
              >
                {cat.name}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Return to Hipobuy Spreadsheet Hub
            </button>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Visit hipobuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          <nav className="mt-8 flex flex-wrap gap-x-4 gap-y-2">
            <strong className="text-xs text-foreground-400 font-body self-center">Quick Navigation:</strong>
            <button onClick={() => navigate('/')} className="text-xs text-foreground-500 hover:text-accent-600 transition-colors underline cursor-pointer font-body">
              Homepage
            </button>
            <span className="text-xs text-foreground-300">|</span>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-accent-600 transition-colors underline font-body">
              Hipobuy Sheet
            </a>
            <span className="text-xs text-foreground-300">|</span>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-accent-600 transition-colors underline font-body">
              Buy It Now
            </a>
            <span className="text-xs text-foreground-300">|</span>
            <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-accent-600 transition-colors underline font-body">
              Shoes
            </a>
            <span className="text-xs text-foreground-300">|</span>
            <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-accent-600 transition-colors underline font-body">
              Hoodies/Sweaters
            </a>
          </nav>
        </div>

        <div className="mt-12 p-6 bg-background-100 rounded-2xl border border-background-200">
          <h3 className="text-base font-semibold text-foreground-900 font-body mb-4">More Articles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {homepageArticles
              .filter((a) => a.id !== articleId)
              .slice(0, 4)
              .map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/article/${a.id}`)}
                  className="text-left p-3 rounded-xl bg-background-50 border border-background-200 hover:border-accent-200 transition-colors cursor-pointer"
                >
                  <span className="text-xs text-foreground-400 font-body">{a.category}</span>
                  <p className="mt-1 text-sm font-medium text-foreground-800 font-body leading-snug">{a.title}</p>
                </button>
              ))}
          </div>
        </div>
      </article>

      <footer className="border-t border-background-200 py-8 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs text-foreground-400 font-body">
            &copy; {new Date().getFullYear()} hipobuyspread.hair. All rights reserved.
          </p>
        </div>
      </footer>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: articleData.title,
            description: articleData.summary,
            datePublished: '2026-07-11',
            author: {
              '@type': 'Organization',
              name: 'Hipobuy Spreadsheet Hub',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Hipobuy Spreadsheet Hub',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hipobuyspread.hair/article/${articleId}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: articleData.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </main>
  );
}